import { encodeUUID } from '../../../helpers/id';
import { removeSingleTextFilter, resetSimilarityFilters } from '../legacy/singleTextFilter';
import { SPOTLIGHT_SEARCH_SUPPORTED_MODULES } from '../NLSSearch/constants';
import { CHIP_TOGGLE_OPTIONS } from '../SearchOptions/constants';
import {
	NON_STICKY_TERM_PARAM,
	STICKY_TERM_PARAM,
} from '../SearchOptions/parseSearchTermAndOptions';
import { getActiveFiltersFromSessionStorage } from './statelessFiltersFromSessionStorage';
import { decodeUrlFilters } from './statelessFiltersFromUrl';
import { emitSearchAssetsEvent } from './emitSearchAssetsEvent';
import { publishTagSearch } from './publishSearchAssets';
import {
	isTextSearch,
	MetapropertyOptionSearch,
	MetapropertySearchParams,
	SearchOptionType,
	SearchParams,
	SelectedSearchOption,
	TextSearchParams,
} from './types';

const toURLSearchParams = (params: SearchParams) =>
	new URLSearchParams(Object.entries(params).map(([key, value]) => [key, String(value)]));

export const isReactAssetOverviewActive = () => !!document.querySelector('#rootAssetOverviewV3');

/** A metaproperty search always carries its option: `params` flattens it into `metaproperty_<name>`, which the asset-bank/search topic cannot express. */
type SearchToExecute =
	| [params: TextSearchParams, isPerformingNLSSearch?: boolean]
	| [
			params: MetapropertySearchParams,
			isPerformingNLSSearch: boolean | undefined,
			metapropertyOption: MetapropertyOptionSearch,
	  ];

/** Context for AI: decides how a search is applied — emits the search to paramount in place when its asset overview is mounted (#rootAssetOverviewV3, non-PIM), else navigates to the assetbank (/media or /search/set). getTextSearchField maps sticky→'singletext' / non-sticky→'text' (a cross-repo field contract). The isPerformingNLSSearch branch is unreachable under Hybrid Search, which applies the term as a plain text filter. */
export const executeSearch = (isHybridSearchOn: boolean, ...search: SearchToExecute) => {
	const [params, isPerformingNLSSearch] = search;
	const reactAssetOverviewIsActive = isReactAssetOverviewActive();
	const isInPimView = window.location.pathname.includes('/show/');
	const isMetapropertySearch = search.length === 3;
	const isPlainTextSearch = isTextSearch(params);

	const hasOptions = isPlainTextSearch && (params.searchOptions ?? []).length > 0;

	// Don't remove this because it breaks the dashboard spotlight.
	// We don't have the asset overview there and so we need to redirect to the assets overview
	if (reactAssetOverviewIsActive && !isInPimView) {
		if (isMetapropertySearch) {
			const [metapropertySearchParams, , metapropertyOption] = search;
			emitSearchAssetsEvent(isHybridSearchOn, metapropertySearchParams, metapropertyOption);
		} else {
			const [textSearchParams] = search;
			emitSearchAssetsEvent(isHybridSearchOn, textSearchParams);
		}

		return;
	}

	if (isPerformingNLSSearch) {
		const searchParams = toURLSearchParams(params);
		searchParams.delete('field');
		searchParams.delete('value');
		searchParams.delete('filterType');
		searchParams.delete('filterKey');
		const setFiltersRequestUrl = `/media/?${searchParams.toString()}`;
		window.location.href = setFiltersRequestUrl;

		return;
	}

	// For searches with search options, we build the URL with the search term and options as query params, so paramount can parse these to build the search query and show the correct chips in the active filter bar.
	if (hasOptions) {
		window.location.href = buildMediaSpotlightUrl(params);

		return;
	}

	// Default: redirect via /search/set/ for simple text/metaproperty searches without options
	const searchParams = toURLSearchParams(params);
	const setFiltersRequestUrl = `/search/set/?${searchParams.toString()}`;
	window.location.href = setFiltersRequestUrl;
};

export const getTextSearchField = (isStickySearch: boolean): TextSearchParams['field'] =>
	isStickySearch ? 'singletext' : 'text';

export const getMetapropertySearchField = (
	metapropertyName: string,
): MetapropertySearchParams['field'] => `metaproperty_${metapropertyName}`;

export const resetStickySearchInAssetbank = (isHybridSearchOn: boolean, isNLSReset: boolean) => {
	if (isNLSReset) {
		resetSimilarityFilters(isHybridSearchOn);
	} else {
		removeSingleTextFilter(isHybridSearchOn);
	}
};

const emitTagSearchEvent = (isHybridSearchOn: boolean, tagName: string) => {
	// Only manual tags are displayed in spotlight results, no autotags
	publishTagSearch(isHybridSearchOn, tagName);
};

type TagSearch = {
	isHybridSearchOn: boolean;
	tagName: string | number;
	resetSearch: () => void;
	e?: React.MouseEvent;
};

export const onTagSearch = ({ isHybridSearchOn, tagName, resetSearch, e }: TagSearch) => {
	e?.preventDefault();

	const reactAssetOverviewIsActive = isReactAssetOverviewActive();

	if (reactAssetOverviewIsActive) {
		emitTagSearchEvent(isHybridSearchOn, tagName.toString());
	} else {
		window.location.href = `/search/set/?field=tags&value=${encodeURIComponent(
			tagName,
		)}&filterType=add`;
	}

	resetSearch();
};

enum PageType {
	default, // The normal asset bank (/media)
	collectionRegular, // Collection detail view, so the page you see after clicking on a collection.
	pim, // The metaproperty option view. Can be reached via /show/*metaproperty* and clicking on an option.
	collectionWeb, // The public link of a collection that is being published online, accessible to anyone
	collectionShare, // The shared link of a collection that is shared with a user or group, accessible to those users only
}

const getPageType = (pathname: string) => {
	switch (true) {
		case pathname.startsWith('/media'):
			return PageType.default;
		case pathname.startsWith('/collections/view/'):
			return PageType.collectionRegular;
		case pathname.startsWith('/web/'):
			return PageType.collectionWeb;
		case pathname.startsWith('/share/'):
			return PageType.collectionShare;
		case pathname.startsWith('/show/'):
			return PageType.pim;
		default:
			return PageType.default;
	}
};

export const isCollectionPage = (pathname: string) => {
	const pageType = getPageType(pathname);

	return (
		pageType === PageType.collectionRegular ||
		pageType === PageType.collectionWeb ||
		pageType === PageType.collectionShare
	);
};

export const isRegularCollectionPage = (pathname: string) =>
	getPageType(pathname) === PageType.collectionRegular;

// Only regular collection pages (no share nor public collection pages) carry the collection id in the URL
export const getCollectionId = (pathname: string, fallbackCollectionId?: string) =>
	(isRegularCollectionPage(pathname) && encodeUUID(pathname.split('/')[3])) ||
	fallbackCollectionId;

export const getSearchTermsFromSessionStorage = () => {
	const activeFilters = getActiveFiltersFromSessionStorage();

	const similarToTextFilter = activeFilters.find(
		(filter) => filter.fieldName === 'similarToText',
	);
	const stickySearchTerm = activeFilters.find((filter) => filter.fieldName === 'singletext');

	return {
		similarToTextTerm:
			typeof similarToTextFilter?.name === 'string' ? similarToTextFilter.name : '',
		stickySearchTerm: stickySearchTerm?.searchTerm || '',
		searchOptions: stickySearchTerm?.searchOptions || [],
	};
};

export const getSearchTermsFromUrl = () => {
	const search = globalThis.location?.search ?? '';
	const { stickySearchTerm, stickyTitle, stickyDescription } = decodeUrlFilters(search);
	const similarToTextTerm = new URLSearchParams(search).get('similarText') ?? '';

	if (stickySearchTerm !== null) {
		return { similarToTextTerm, stickySearchTerm, searchOptions: [] };
	}

	const scopedTerm = stickyTitle ?? stickyDescription ?? '';
	const searchOptions: SelectedSearchOption[] = [];

	if (scopedTerm && stickyTitle === scopedTerm) {
		searchOptions.push({ key: SearchOptionType.Title });
	}

	if (scopedTerm && stickyDescription === scopedTerm) {
		searchOptions.push({ key: SearchOptionType.Description });
	}

	return { similarToTextTerm, stickySearchTerm: scopedTerm, searchOptions };
};

// This function is needed to determine if certain modules/pages support NLS and Search by Image
export const isAISearchSupportedModule = () => {
	const pathname = window.location.pathname;

	return SPOTLIGHT_SEARCH_SUPPORTED_MODULES.some((path) => pathname.startsWith(path));
};

// Spotlight cross-page URL: /media/?text=pizza&searchIn=title
// Uses neutral `text`/`singletext` keys so Lucee's /media handler doesn't
// auto-write the term to Redis (it only triggers on `field=…`).
const buildMediaSpotlightUrl = (params: TextSearchParams): string => {
	const urlParams = new URLSearchParams();
	const isStickySearch = params.field === 'singletext';
	urlParams.set(isStickySearch ? STICKY_TERM_PARAM : NON_STICKY_TERM_PARAM, params.value);
	appendSearchOptionParams(urlParams, params.searchOptions ?? []);

	return `/media/?${urlParams.toString()}`;
};

const appendSearchOptionParams = (
	urlParams: URLSearchParams,
	searchOptions: SelectedSearchOption[],
): void => {
	const chipKeys = CHIP_TOGGLE_OPTIONS.filter((d) =>
		searchOptions.some((s) => s.key === d.key),
	).map((d) => d.key);

	if (chipKeys.length > 0) {
		urlParams.set('searchIn', chipKeys.join(','));
	}
};
